import { useCourtFilingMarks } from "components/CourtFilingMarks";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { editDefendantAppeal } from "services/appealService";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appealId?: number;
  isCassation?: boolean;
  onForwarded: () => void;
};

// Inline panel (replaces the registrar "Forward to Court" modal): the registrar
// enters the registration number + date and forwards the appeal to court.
const DefendantForwardToCourtPanel: FC<Props> = ({
  appealId,
  isCassation,
  onForwarded,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();

  const courtLabel = isCassation
    ? t("forward_to_cassation_court", {
        defaultValue: "Forward to Cassation Court",
      })
    : t("forward_to_court");
  const [loading, setLoading] = useState(false);
  const [registrarNumber, setRegistrarNumber] = useState("");
  // Office stamp and signature printed on the response filed to court.
  const courtMarks = useCourtFilingMarks(true);
  const [registrarDate, setRegistrarDate] = useState("");

  const handleSubmit = () => {
    if (!registrarNumber.trim()) {
      showTooltip(t("registrar_number_is_required"), "error");
      return;
    }
    if (!registrarDate) {
      showTooltip(t("registrar_date_is_required"), "error");
      return;
    }
    setLoading(true);
    editDefendantAppeal({
      id: appealId!,
      registrar_number: registrarNumber,
      // Send ISO datetime (backend rejects the JS Date toString format).
      registrar_date: new Date(registrarDate).toISOString(),
      status: "FORWARDED_TO_COURT",
      ...courtMarks.marks,
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onForwarded();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      )
      .finally(() => setLoading(false));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-base">🏛️</span>
        <h4 className="text-sm font-semibold text-gray-900">{courtLabel}</h4>
      </div>
      <p className="text-xs text-gray-500">
        {t("forward_to_court_desc", {
          defaultValue:
            "Enter the court registration number and date to forward this appeal to court.",
        })}
      </p>

      <div className="flex flex-col">
        <label className="text-xs font-medium text-gray-600 mb-1">
          {t("registrar_number")}
          <span className="text-red-500"> *</span>
        </label>
        <input
          type="text"
          value={registrarNumber}
          onChange={(e) => setRegistrarNumber(e.target.value)}
          className="border border-gray-300 rounded-lg bg-white p-2 text-sm focus:outline-none focus:ring focus:ring-red-200"
        />
      </div>

      <div className="flex flex-col">
        <label className="text-xs font-medium text-gray-600 mb-1">
          {t("registrar_date")}
          <span className="text-red-500"> *</span>
        </label>
        <input
          type="date"
          value={registrarDate}
          onChange={(e) => setRegistrarDate(e.target.value)}
          className="border border-gray-300 rounded-lg bg-white p-2 text-sm focus:outline-none focus:ring focus:ring-red-200"
        />
      </div>

      {courtMarks.fields}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              {LoadingIcon}
              {t("loading")}
            </>
          ) : (
            `🏛️ ${courtLabel}`
          )}
        </button>
      </div>
    </div>
  );
};

export default DefendantForwardToCourtPanel;
